<template>
<div>
  <Loading
        v-model:active="loading"
        :can-cancel="false"
        :widh="100"
        :height="100"
        :is-full-page="true"
        :opacity="0.7"
        color="#0dcaf0"
        background-color="#000"
  ></Loading>
  <div class="survey-wrap">
    <template v-if="valid">
      <!-- ── Header ── -->
      <div class="survey-header">
        <h1 class="survey-title">EVOASIS 安心裝服務<br>顧客滿意度問卷</h1>
        <p class="survey-subtitle">
          感謝您選擇 EVOASIS AC 充電樁安裝服務！為了持續提升服務品質，誠摯邀請您花 3
          分鐘填寫以下問卷，您的意見是我們進步的動力。
        </p>
      </div>

      <!-- ── Form ── -->
      <form v-if="!submitted" @submit.prevent="handleSubmit" novalidate>

        <!-- Part 1 · 基本資料 -->
        <SurveySection :number="1" title="基本資料">
          <div class="q-item">
            <label class="q-label" for="q1">
              <span class="q-num">Q1</span> 您的姓名
              <span class="q-required">*</span>
            </label>
            <input
              id="q1"
              v-model="form.q1"
              type="text"
              placeholder="請輸入您的姓名"
              :class="{ 'input-error': errors.q1 }"
            />
            <p v-if="errors.q1" class="error-msg">此欄位為必填</p>
          </div>

          <div class="q-item">
            <label class="q-label" for="q2">
              <span class="q-num">Q2</span> 您的聯絡電話
              <span class="q-required">*</span>
            </label>
            <input
              id="q2"
              v-model="form.q2"
              type="tel"
              placeholder="請輸入您的聯絡電話"
              :class="{ 'input-error': errors.q2 }"
            />
            <p v-if="errors.q2" class="error-msg">此欄位為必填</p>
          </div>
        </SurveySection>

        <!-- Part 2 · 安裝服務滿意度 -->
        <SurveySection :number="2" title="安裝服務滿意度">
          <RatingQuestion
            v-for="q in installQuestions"
            :key="q.name"
            :qnum="q.num"
            :question="q.label"
            :name="q.name"
            :options="q.options || satisfactionOptions"
            :required="true"
            :modelValue="form[q.name]"
            :error="errors[q.name]"
            @update:modelValue="(val) => (form[q.name] = val)"
          />

          <div class="q-item">
            <label class="q-label" for="q11">
              <span class="q-num">Q11</span>
              服務中有無讓您印象深刻的優點或值得表揚的地方？
            </label>
            <textarea id="q11" v-model="form.q11" placeholder="歡迎分享您的正面體驗…" rows="3" />
          </div>

          <div class="q-item">
            <label class="q-label" for="q12">
              <span class="q-num">Q12</span>
              您覺得我們的安裝服務有哪些地方可以改進？
            </label>
            <textarea id="q12" v-model="form.q12" placeholder="您寶貴的改善建議…" rows="3" />
          </div>
        </SurveySection>

        <!-- Part 3 · 品牌資訊來源與選擇原因 -->
        <SurveySection :number="3" title="品牌資訊來源與選擇原因">
          <div class="q-item">
            <p class="q-label">
              <span class="q-num">Q13</span>
              您是如何得知 EVOASIS AC 充電樁的？（可複選）
            </p>
            <div class="checkbox-group">
              <label v-for="opt in q13Options" :key="opt.value" class="check-opt">
                <input type="checkbox" :value="opt.value" v-model="form.q13" />
                {{ opt.label }}
              </label>
              <label class="check-opt">
                <input type="checkbox" value="其他" v-model="form.q13" />
                其他：
                <input
                  type="text"
                  v-model="form.q13Other"
                  placeholder="請說明"
                  class="inline-other"
                  :disabled="!form.q13.includes('其他')"
                />
              </label>
            </div>
          </div>

          <div class="q-item">
            <p class="q-label">
              <span class="q-num">Q14</span>
              您選擇 EVOASIS AC 充電樁的原因？（可複選）
            </p>
            <div class="checkbox-group">
              <label v-for="opt in q14Options" :key="opt.value" class="check-opt">
                <input type="checkbox" :value="opt.value" v-model="form.q14" />
                {{ opt.label }}
              </label>
              <label class="check-opt">
                <input type="checkbox" value="其他" v-model="form.q14" />
                其他：
                <input
                  type="text"
                  v-model="form.q14Other"
                  placeholder="請說明"
                  class="inline-other"
                  :disabled="!form.q14.includes('其他')"
                />
              </label>
            </div>
          </div>
        </SurveySection>

        <!-- Part 4 · 官網資訊內容 -->
        <SurveySection :number="4" title="官網資訊內容">
          <RatingQuestion
            qnum="Q15"
            question="官網內容是否清楚易懂？"
            name="q15"
            :options="clarityOptions"
            :required="true"
            v-model="form.q15"
            :error="errors.q15"
          />
          <RatingQuestion
            qnum="Q16"
            question="官網上商品圖片與說明，是否符合您的需求？"
            name="q16"
            :options="matchOptions"
            :required="true"
            v-model="form.q16"
            :error="errors.q16"
          />
          <RatingQuestion
            qnum="Q17"
            question="官網上的說明書內容，是否符合您的需求？"
            name="q17"
            :options="matchOptions"
            :required="true"
            v-model="form.q17"
            :error="errors.q17"
          />

          <div class="q-item">
            <label class="q-label" for="q18">
              <span class="q-num">Q18</span> 您覺得官網有哪些地方可以改進？
            </label>
            <textarea id="q18" v-model="form.q18" placeholder="您對官網的改善建議…" rows="3" />
          </div>
        </SurveySection>

        <!-- Submit -->
        <div class="submit-section">
          <button type="submit" class="submit-btn">完成並送出問卷</button>
          <p class="submit-hint">感謝您撥冗填寫本問卷！<br />您的寶貴意見將協助我們持續提升服務品質。</p>
        </div>
      </form>

      <!-- ── Thank You ── -->
      <div v-else class="thank-you">
        <div class="ty-icon">✓</div>
        <p class="ty-title">感謝您填寫問卷！</p>
        <p class="ty-sub">您的寶貴意見已收到，<br />將協助我們持續提升 EVOASIS 的服務品質。</p>
      </div>
    </template>
    <template v-else-if="!loading">
      <div class="thank-you">
        <div class="ty-icon">✖︎</div>
        <p class="ty-title">作業失敗</p>
        <p class="ty-sub">此問卷連結已失效，如有任何問題，歡迎聯繫EVOASIS，將有專員為您協助。</p>
      </div>
    </template>
  </div>
</div>
  
</template>

<script setup>
import { reactive, onBeforeMount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import Loading from 'vue-loading-overlay'
import axios from "axios";
import SurveySection from "@/components/SurveySection.vue"
import RatingQuestion from "@/components/RatingQuestion.vue"
const route = useRoute()
const loading = ref(true)
const valid = ref(false)
const orderId = ref(route.params.orderId)
// ── Constants ──────────────────────────────────────────────────────────────

const satisfactionOptions = ['非常滿意', '滿意', '普通', '不滿意']
const recommendOptions    = ['一定會', '可能會', '不確定', '不會']
const clarityOptions      = ['非常清楚', '清楚', '普通', '不清楚']
const matchOptions        = ['非常符合', '符合', '普通', '不符合']

const installQuestions = [
  { num: 'Q3',  name: 'q3',  label: '安裝前的預約與溝通安排' },
  { num: 'Q4',  name: 'q4',  label: '安裝人員的專業說明與現場報價透明度' },
  { num: 'Q5',  name: 'q5',  label: '安裝人員的服務態度' },
  { num: 'Q6',  name: 'q6',  label: '實際施工的品質' },
  { num: 'Q7',  name: 'q7',  label: '工程現場的整潔與安全措施' },
  { num: 'Q8',  name: 'q8',  label: '安裝完成後的功能測試與操作說明' },
  { num: 'Q9',  name: 'q9',  label: '您對整體安裝服務的滿意程度為' },
  { num: 'Q10', name: 'q10', label: '您會推薦我們的安裝服務給親友嗎？', options: recommendOptions },
]

const q13Options = [
  { value: '官方網站',      label: '官方網站' },
  { value: '親友推薦',      label: '親友推薦' },
  { value: '社群媒體',      label: '社群媒體' },
  { value: '實體廣告',      label: '實體廣告' },
  { value: '數位廣告',      label: '數位廣告' },
  { value: '車商/經銷商推薦', label: '車商／經銷商推薦' },
  { value: '網路搜尋',      label: '網路搜尋' },
]

const q14Options = [
  { value: '品牌信賴度',   label: '品牌信賴度' },
  { value: '產品規格與功能', label: '產品規格與功能' },
  { value: '價格合理',     label: '價格合理' },
  { value: '安裝服務完善', label: '安裝服務完善' },
  { value: '親友推薦',     label: '親友推薦' },
  { value: '售後保固',     label: '售後保固' },
]

// ── State ──────────────────────────────────────────────────────────────────

const submitted = ref(false)

const form = reactive({
  q1: '', q2: '',
  q3: '', q4: '', q5: '', q6: '', q7: '', q8: '', q9: '', q10: '',
  q11: '', q12: '',
  q13: [], q13Other: '',
  q14: [], q14Other: '',
  q15: '', q16: '', q17: '',
  q18: '',
})

const errors = reactive({
  q1: false, q2: false,
  q3: false, q4: false, q5: false, q6: false,
  q7: false, q8: false, q9: false, q10: false,
  q15: false, q16: false, q17: false,
})

// ── Validation & Submit ────────────────────────────────────────────────────

const requiredFields = ['q1','q2','q3','q4','q5','q6','q7','q8','q9','q10','q15','q16','q17']

const handleSubmit = async() => {
  loading.value = true
  let formValid = true
  requiredFields.forEach(f => {
    const val = form[f]
    errors[f] = typeof val === 'string' ? !val.trim() : !val
    if (errors[f]) formValid = false
  })
  if (!formValid) {
    document.querySelector('.input-error, .error-msg')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }

  const { data: _response } = await axios.post(`https://api.evoasis.com.tw/portal/wix/order/survey`, {
      orderId: orderId.value,
      ...form
  })
  loading.value = false
  submitted.value = true
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const fetchOrderData = async() => {
    const { data: response } = await axios.get(`https://api.evoasis.com.tw/portal/wix/order/${orderId.value}/survey`)
    loading.value = false
    if(!response?.message) {
        valid.value = true
        form.q1 = response.name
        form.q2 = response.phone 
    }
}

onMounted(() => document.title = route.meta.title)
onBeforeMount(async () => {
    try {
        await fetchOrderData()
    } catch (error) {
        console.log(error);
    }
});

</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.survey-wrap {
  max-width: 680px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
  font-family: 'Noto Sans TC', sans-serif;
  color: #1a1a1a;
}

/* ── Header ── */
.survey-header {
  background: linear-gradient(135deg, #0a3d62 0%, #1a6aa8 60%, #2196c4 100%);
  border-radius: 12px;
  padding: 2rem 2rem 1.75rem;
  margin-bottom: 1.5rem;
  color: #fff;
  position: relative;
  overflow: hidden;
}
.survey-header::before {
  content: '';
  position: absolute;
  top: -40px; right: -40px;
  width: 160px; height: 160px;
  border-radius: 50%;
  background: rgba(255,255,255,0.07);
  pointer-events: none;
}
.survey-title {
  font-size: 20px;
  font-weight: 500;
  color: #fff;
  margin-bottom: 0.75rem;
  line-height: 1.5;
}
.survey-subtitle {
  font-size: 13px;
  color: rgba(255,255,255,0.75);
  line-height: 1.7;
  margin: 0;
}

/* ── Question items (used directly in this file) ── */
.q-item {
  padding: 1.1rem 1.25rem;
  border-bottom: 0.5px solid #f0f0f0;
}
.q-item:last-child { border-bottom: none; }

.q-label {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0 0 0.65rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}
.q-num      { color: #1a6aa8; font-weight: 500; }
.q-required { color: #c0392b; font-size: 11px; }

/* ── Text inputs & textarea ── */
input[type='text'],
input[type='tel'],
textarea {
  width: 100%;
  padding: 9px 12px;
  border: 0.5px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: #1a1a1a;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
}
input[type='text']:focus,
input[type='tel']:focus,
textarea:focus {
  border-color: #1a6aa8;
  box-shadow: 0 0 0 3px rgba(26,106,168,0.12);
}
input.input-error { border-color: #e74c3c; }
textarea { resize: vertical; line-height: 1.6; }
.error-msg { font-size: 12px; color: #e74c3c; margin-top: 4px; }

/* ── Checkboxes ── */
.checkbox-group { display: flex; flex-direction: column; gap: 8px; }
.check-opt {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #1a1a1a;
  cursor: pointer;
}
.check-opt input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: #1a6aa8;
  cursor: pointer;
  flex-shrink: 0;
}
.inline-other {
  flex: 1;
  padding: 5px 10px;
  font-size: 13px;
  width: auto;
}
.inline-other:disabled { background: #f5f5f5; color: #aaa; }

/* ── Submit ── */
.submit-section { padding: 1.5rem 0 0; text-align: center; }
.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #1a6aa8;
  color: #fff;
  border: none;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}
.submit-btn:hover  { background: #145d96; }
.submit-btn:active { transform: scale(0.98); }
.submit-hint {
  margin-top: 0.75rem;
  font-size: 12px;
  color: #aaa;
}

/* ── Thank You ── */
.thank-you { background: white; border-radius: 12px; text-align: center; padding: 3rem 1rem; }
.ty-icon {
  width: 64px; height: 64px;
  background: #e8f3fb;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1.25rem;
  font-size: 28px;
  color: #1a6aa8;
  font-weight: 500;
}
.ty-title { font-size: 20px; font-weight: 500; color: #1a1a1a; margin-bottom: 0.5rem; }
.ty-sub   { font-size: 14px; color: #888; line-height: 1.7; }

@media (max-width: 480px) {
  .survey-header { padding: 1.5rem 1.25rem; }
}
</style>